import { Button, Checkbox, Form, Input, Spin } from 'antd';
import crypto from 'crypto-js';
import { useEffect, useState } from 'react';

import { subscribeNewsletter } from 'actions/general';

import FormInput from 'components/formInput';
import OldProtectionDataModal from 'components/protectionDataModal';
import CountdownTimer from 'components/timer';
import { useI18n } from 'context/i18n';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { NnormalSVG } from 'components/iconsSVG/nnormal';
import { CamperSVG } from 'components/iconsSVG/camper';

import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { CAMPAIGNS_WITH_LOGO, CAMPAIGN_STATUS } from 'utils/constants/campaigns';
import { getIsMobile } from 'utils/helpers';

import { useLocale, useValidate } from 'hooks';
import { PrivacyPolicyText } from 'components/legalTexts';
import { basePropTypes } from './props';
import { storage } from 'utils/web-storage';
import { LOCAL_STORAGE_KEYS } from 'utils/constants/storage';

import styles from './style.module.css';

const Logo = IS_NNORMAL ? NnormalSVG : CamperSVG;
const { SHA256 } = crypto;

const genreLabel = ['women', 'men', 'kids', 'accesories'];

const CAMPAIGN_WITHOUT_TITLE = ['mss'];

function GenericCampaing({ campaign, tnmProps }) {
  const { t, profileData } = useI18n();
  const { locale, country, language } = useLocale();
  const validate = useValidate();
  const urlPrivacyPolicy =
    IS_NNORMAL ? profileData.urlPrivacyPolicy : `/html/legal/pdfs/privacy/privacy_${country.toUpperCase()}_${language.toUpperCase()}.pdf`;
  const [viewPort, setViewPort] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alreadySub, setAlreadySub] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false);
  const [dataModalVisible, setDataModalVisible] = useState(false);

  const lowerThanDesktop = getIsMobile('1279.5px');
  const lowerThanTablet = getIsMobile('767.5px');

  const [emailForm] = Form.useForm();
  const origin = `${campaign.campaignStatus.toLowerCase()}-landing-${campaign.id}`;

  const onFinishForm = async (values) => {
    setLoading(true);
    const payload = {
      email: values.email,
      profile: {
        countryId: country,
        languageId: language,
      },
      origin,
    };

    try {
      const result = await subscribeNewsletter(payload);

      if (result.contactAlreadySubscribed) {
        setAlreadySub(true);
      } else {
        setAlreadySub(false);

        dataLayerHandleEvent({
          event: 'mailShadowed',
          mailSHA256: SHA256(values?.email?.toLowerCase()).toString(),
        });
        storage.local.set(LOCAL_STORAGE_KEYS.USER_SUBSCRIBED, true);

        const eventGA4 = {
          event: 'acquisition_landing',
          info_type: 'suscription',
          campaign_detail: campaign?.name?.toLowerCase() || '',
          page_type: 'landing',
        };
        dataLayerHandleEvent(eventGA4);

        setSuccess(true);
        if (campaign.id === 'arco' && process.browser) {
          setRedirectLoading(true);

          setTimeout(() => {
            window.location.assign(`${window.location.origin}/${locale}/landings/pre-archive_collection`);
          }, 3000);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      const eventGA4 = {
        event: 'acquisition_landing',
        info_type: 'error',
        campaign_detail: campaign?.name?.toLowerCase() || '',
        page_type: 'landing',
        info_error: error,
      };
      dataLayerHandleEvent(eventGA4);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (process.browser) {
      let viewPortString = null;

      if (lowerThanDesktop === false) {
        viewPortString = 'desktop';
      } else if (lowerThanDesktop === true && lowerThanTablet === false) {
        viewPortString = 'tablet';
      } else if (lowerThanDesktop === true && lowerThanTablet === true) {
        viewPortString = 'mobile';
      }

      setViewPort(viewPortString);
    }
  }, [process.browser, lowerThanDesktop, lowerThanTablet]);

  const isPostArco = campaign.id === 'arco' && campaign.campaignStatus === CAMPAIGN_STATUS.POST;
  let imageSource = `/assets-new/${campaign.campaignStatus.toLowerCase()}-${campaign.id}`;
  if (isPostArco || campaign.id === 'mss') {
    imageSource = `/assets-new/${campaign.id}`;
  }
  const isVideo = ['blfr', 'cym', 'sales', 'fafr'].includes(campaign.id);
  let shoppingLinks = [];
  if (isPostArco) {
    const menuTypes = ['W', 'M', 'K'];
    shoppingLinks = tnmProps?.menus?.filter((menu) => menuTypes.includes(menu.menuType));
    const womenLink = shoppingLinks.filter((menu) => menu.menuType === 'W').shift();
    if (womenLink) {
      const accessoriesLink = womenLink.groups?.filter((menu) => menu.tracking.includes('accessories'))?.shift();
      if (accessoriesLink) {
        shoppingLinks.push(accessoriesLink);
      }
    }
  }

  return (
    <main
      className={`${styles.main} ${campaign.id === 'oldsales' ? styles.reversed : ''} ${campaign.id === 'sales' ? styles.mainSales : ''}`}
      data-campaign={campaign.id}
    >
      <div className={`${styles.imgWrapper} ${isVideo ? styles.videoWrapper : ''}`}>
        {viewPort !== null && (
          <>
            {isVideo ?
              <video
                className={styles.headerPhoto}
                autoPlay
                loop
                title={`Video of ${campaign.id}`}
                poster={`${imageSource}-${viewPort}.webp`}
                muted
                playsInline
              >
                <source src={`${imageSource}-${viewPort}.webm`} type="video/webm"></source>
                <source src={`${imageSource}-${viewPort}.mp4`} type="video/mp4"></source>
              </video>
            : <picture>
                <img className={styles.headerPhoto} src={`${imageSource}-${viewPort}.jpg`} alt="campaign photo" />
              </picture>
            }
            {(!isVideo && CAMPAIGN_WITHOUT_TITLE.includes(campaign.id)) || isPostArco ? null : (
              <div className={styles.imgText}>
                {CAMPAIGNS_WITH_LOGO.includes(campaign.id) && <Logo className={styles.logo} />}
                <span className={styles.imgTitle}>
                  {t('listado.productos', `landing.title.${campaign.id}.${campaign.campaignStatus.toLowerCase()}`, '***Campaign Title')}
                </span>
                <span className={styles.imgSubtitle}>
                  {t('listado.productos', `landing.subtitle.${campaign.id}.${campaign.campaignStatus.toLowerCase()}`, '***Campaign Subtitle')}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      {success ?
        <div className={`${styles.modalContentThanks} ${styles[campaign.id]}`}>
          <Spin spinning={redirectLoading} size="large" />
          <div className={styles.svgWrapper}>
            <img src="/assets-new/campaign-subscription-socks.gif" alt="Subscription OK!" width={90} height={90} />
          </div>
          <p className={styles.titleThanks}>
            {t('generico', IS_CAMPER ? 'camper.newsletter.agradecimiento.final' : 'newsletter.agradecimiento.final', 'Thank you!')}
          </p>
          <p className={styles.subtitleThanks}>
            {isVideo ?
              t('generico', IS_CAMPER ? 'camper.newsletter.redirect' : 'newsletter.redirect', '***You will be redirected shortly.')
            : t(
                'generico',
                IS_CAMPER ? 'camper.newsletter.agradecimiento.subtitulo' : 'newsletter.agradecimiento.subtitulo',
                'Your subscription finished successfully. You will receive a confirmation email shortly.',
              )
            }
          </p>
        </div>
      : <div className={`${styles.customContent} ${styles[campaign.id]} ${styles[campaign.campaignStatus.toLowerCase()]}`}>
          {campaign.campaignStatus !== CAMPAIGN_STATUS.OPEN && (
            <>
              <p
                className={styles.title}
                dangerouslySetInnerHTML={{
                  __html: t('listado.productos', `landing.text.title.${campaign.id.toLowerCase()}.${campaign.campaignStatus.toLowerCase()}`).replace(
                    /\n/g,
                    '<br />',
                  ),
                }}
              ></p>
              <p
                className={styles.text}
                dangerouslySetInnerHTML={{
                  __html: t('listado.productos', `landing.text.1.${campaign.id.toLowerCase()}.${campaign.campaignStatus.toLowerCase()}`).replace(
                    /\n/g,
                    '<br />',
                  ),
                }}
              ></p>
              {campaign.countDown === true && <CountdownTimer countdown={campaign?.countDownEnd} />}
              <p
                className={styles.text}
                dangerouslySetInnerHTML={{
                  __html: t('listado.productos', `landing.text.2.${campaign.id.toLowerCase()}.${campaign.campaignStatus.toLowerCase()}`).replace(
                    /\n/g,
                    '<br />',
                  ),
                }}
              ></p>
              <p
                className={styles.text}
                dangerouslySetInnerHTML={{
                  __html: t('listado.productos', `landing.text.3.${campaign.id.toLowerCase()}.${campaign.campaignStatus.toLowerCase()}`).replace(
                    /\n/g,
                    '<br />',
                  ),
                }}
              ></p>
            </>
          )}
          {isPostArco && (
            <div className={styles.shoppingLinks}>
              {shoppingLinks?.map((menu) => (
                <a
                  key={menu?.title}
                  href={menu?.slug || `/${locale}`}
                  data-tracking={menu?.tracking?.replace('main-menu', 'arco-landing')}
                  data-trackingga4={menu?.trackingGA4?.replace('main-menu', 'arco-landing')}
                >
                  {menu?.title !== 'CAMPERLAB' ? menu?.title : 'Camperlab'}
                </a>
              ))}
            </div>
          )}
          {campaign.id === 'sales' &&
            campaign.campaignStatus === CAMPAIGN_STATUS.POST && ( // cambiar a PRE antes de subir
              <div className={styles.salesBtnGroupWrapper}>
                <p>{t('listado.productos', 'shop', 'Shop')}</p>
                <div className={styles.salesBtnGroup}>
                  {campaign.collections.map((collection, index) => {
                    if (index > 3) {
                      return null;
                    }
                    return (
                      <Button className={styles.btnPreSales} key={collection.id}>
                        <a href={collection.urlSeeMore}>{t('mi.cuenta', genreLabel[index])}</a>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          {!isPostArco && (
            <Form
              form={emailForm}
              layout="vertical"
              name="emailForm"
              onFinish={onFinishForm}
              requiredMark="optional"
              scrollToFirstError
              className={styles.emailForm}
            >
              <FormInput name="email" rules={validate('email')} required className={styles.emailInput}>
                <Input placeholder={t('generico', 'input.email.placeholder', 'Enter your email')} type="email" autoComplete="false" formNoValidate />
              </FormInput>

              <div className={styles.privacyWrapper}>
                <a onClick={() => setDataModalVisible(true)} className={styles.policyLink}>
                  {t('generico', IS_CAMPER ? 'camper.newsletter.proteccion.datos' : 'newsletter.proteccion.datos', 'Data protection information')}
                </a>
              </div>

              <Form.Item name="privacy" valuePropName="checked" rules={validate('privacyPolicy')}>
                <Checkbox tabIndex="27">
                  {country === 'US' ?
                    <PrivacyPolicyText styles={styles.privacyAgreement} />
                  : <>
                      <span className={styles.privacyAgreement}>{t('generico', 'acepto.terminos', 'I accept and understand the ')} </span>
                      <a className={styles.boldLink} href={urlPrivacyPolicy} target="_blank" rel="noreferrer">
                        {t('generico', 'privacy.conditions', 'privacy conditions')}
                      </a>
                    </>
                  }
                </Checkbox>
              </Form.Item>

              {alreadySub && <span className={`error ${styles.emailError}`}>{t('newsletter', 'gracias.existe.mail')}</span>}

              <Spin spinning={loading}>
                <Form.Item className={styles.submitItem}>
                  <Button type="default" htmlType="submit" className={campaign.id === 'sales' ? `${styles.submitBtn} ${styles.btnPreSales}` : styles.submitBtn}>
                    {t('generico', 'subscribe', 'Subscribe')}
                  </Button>
                </Form.Item>
              </Spin>

              {campaign?.countDown === true && (
                <p
                  className={styles.text}
                  dangerouslySetInnerHTML={{
                    __html: t('listado.productos', `landing.valid.from.to.${campaign.id.toLowerCase()}.${country}`).replace(/\n/g, '<br />'),
                  }}
                ></p>
              )}
            </Form>
          )}
          <OldProtectionDataModal visible={dataModalVisible} onCancel={() => setDataModalVisible(false)} origin="newsletter" />
        </div>
      }
    </main>
  );
}

GenericCampaing.propTypes = {
  ...basePropTypes,
};

export default GenericCampaing;
